import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import apiService from '../services/api';
import Modal from './Modal';
import { formatLivraisonPlageLabel } from '../utils/livraisonCommandeDisplay';

const JOUR_OPTIONS = [
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
  { value: 0, label: 'Dimanche' },
];

const formatPlage = (debut, fin) => formatLivraisonPlageLabel(debut, fin) || `${debut} – ${fin}`;

function capaciteLabel(capacite) {
  if (capacite == null) return 'Illimité';
  const n = Number(capacite);
  if (!Number.isInteger(n) || n < 1) return 'Illimité';
  return n === 1 ? 'Maximum : 1 livraison' : `Maximum : ${n} livraisons`;
}

const LivraisonCreneauxManager = ({ commercantId, reloadToken = 0, onCreneauxChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creneaux, setCreneaux] = useState([]);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    jourSemaine: 1,
    heureDebut: '11:30',
    heureFin: '13:30',
    capaciteMode: 'unlimited',
    capaciteMax: '4',
  });

  const byDay = useMemo(() => {
    const map = {};
    JOUR_OPTIONS.forEach((day) => {
      map[day.value] = [];
    });
    creneaux.forEach((item) => {
      if (!map[item.jourSemaine]) map[item.jourSemaine] = [];
      map[item.jourSemaine].push(item);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => String(a.heureDebut).localeCompare(String(b.heureDebut)));
    });
    return map;
  }, [creneaux]);

  const loadData = async () => {
    if (!commercantId) return;
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getLivraisonCreneaux(commercantId);
      const next = response?.creneaux || [];
      setCreneaux(next);
      if (typeof onCreneauxChange === 'function') {
        onCreneauxChange(next);
      }
    } catch (err) {
      setError(err?.message || 'Impossible de charger les créneaux de livraison.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercantId, reloadToken]);

  const openCreate = (jourSemaine) => {
    setEditing(null);
    setForm({
      jourSemaine,
      heureDebut: '18:30',
      heureFin: '20:00',
      capaciteMode: 'unlimited',
      capaciteMax: '4',
    });
    setModalOpen(true);
  };

  const openEdit = (creneau) => {
    setEditing(creneau);
    const limited = creneau.capacite != null && Number(creneau.capacite) >= 1;
    setForm({
      jourSemaine: creneau.jourSemaine,
      heureDebut: creneau.heureDebut,
      heureFin: creneau.heureFin,
      capaciteMode: limited ? 'limited' : 'unlimited',
      capaciteMax: limited ? String(creneau.capacite) : '4',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (form.capaciteMode === 'limited') {
        const n = Number(form.capaciteMax);
        if (!Number.isInteger(n) || n < 1) {
          setError('Indiquez un nombre entier d’au moins 1 livraison.');
          return;
        }
      }
      const payload = {
        jourSemaine: Number(form.jourSemaine),
        heureDebut: String(form.heureDebut || '').slice(0, 5),
        heureFin: String(form.heureFin || '').slice(0, 5),
        capacite: form.capaciteMode === 'limited' ? Number(form.capaciteMax) : null,
      };
      if (editing) {
        await apiService.updateLivraisonCreneau(commercantId, editing.id, payload);
        setFeedback('Créneau modifié.');
      } else {
        await apiService.createLivraisonCreneau(commercantId, payload);
        setFeedback('Créneau ajouté.');
      }
      setModalOpen(false);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Impossible d’enregistrer le créneau.');
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(''), 4000);
    }
  };

  const handleDelete = async (creneau) => {
    const ok = window.confirm(`Supprimer le créneau ${formatPlage(creneau.heureDebut, creneau.heureFin)} ?`);
    if (!ok) return;
    try {
      setError('');
      await apiService.deleteLivraisonCreneau(commercantId, creneau.id);
      setFeedback('Créneau supprimé.');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Impossible de supprimer le créneau.');
    } finally {
      setTimeout(() => setFeedback(''), 4000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
        Créneaux de livraison
      </h4>
      <p className="mt-1 text-xs text-gray-500">
        Le client choisira une plage complète, jamais une heure précise. La capacité se règle par créneau.
      </p>

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {feedback && (
        <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          {feedback}
        </div>
      )}

      <div className="mt-4 space-y-4">
        {JOUR_OPTIONS.map((day) => (
          <div key={day.value} className="rounded-md border border-gray-100 p-3">
            <div className="mb-2 flex items-center justify-between">
              <h5 className="text-sm font-medium text-gray-900">{day.label}</h5>
              <button
                type="button"
                onClick={() => openCreate(day.value)}
                className="inline-flex items-center text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Ajouter un créneau
              </button>
            </div>
            {(byDay[day.value] || []).length === 0 ? (
              <p className="text-xs text-gray-400">Aucun créneau</p>
            ) : (
              <ul className="space-y-1">
                {(byDay[day.value] || []).map((creneau) => (
                  <li
                    key={creneau.id}
                    className="flex items-center justify-between rounded bg-gray-50 px-2 py-1.5 text-sm text-gray-800"
                  >
                    <span>
                      {formatPlage(creneau.heureDebut, creneau.heureFin)}
                      <span className="ml-2 text-xs text-gray-500">{capaciteLabel(creneau.capacite)}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(creneau)}
                        className="rounded p-1 text-gray-500 hover:bg-white hover:text-blue-600"
                        aria-label="Modifier le créneau"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(creneau)}
                        className="rounded p-1 text-gray-500 hover:bg-white hover:text-red-600"
                        aria-label="Supprimer le créneau"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Modifier le créneau' : 'Ajouter un créneau'}
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Jour</label>
            <select
              value={form.jourSemaine}
              onChange={(e) => setForm((prev) => ({ ...prev, jourSemaine: Number(e.target.value) }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            >
              {JOUR_OPTIONS.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Début</label>
              <input
                type="time"
                value={form.heureDebut}
                onChange={(e) => setForm((prev) => ({ ...prev, heureDebut: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Fin</label>
              <input
                type="time"
                value={form.heureFin}
                onChange={(e) => setForm((prev) => ({ ...prev, heureFin: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Capacité du créneau</label>
            <p className="mb-2 text-xs text-gray-500">
              Nombre maximum de livraisons pour cette plage, à une date donnée.
            </p>
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="livraison-capacite-mode"
                  checked={form.capaciteMode === 'unlimited'}
                  onChange={() => setForm((prev) => ({ ...prev, capaciteMode: 'unlimited' }))}
                  className="mt-1 h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-800">Illimitée</span>
              </label>
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="livraison-capacite-mode"
                  checked={form.capaciteMode === 'limited'}
                  onChange={() => setForm((prev) => ({ ...prev, capaciteMode: 'limited' }))}
                  className="mt-1 h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="flex-1 text-sm text-gray-800">
                  Nombre maximum de livraisons
                  {form.capaciteMode === 'limited' && (
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={form.capaciteMax}
                      onChange={(e) => setForm((prev) => ({ ...prev, capaciteMax: e.target.value }))}
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  )}
                </span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LivraisonCreneauxManager;
